import { useEffect, useMemo, useState, type ReactElement } from "react";

import {
  Badge,
  Box,
  ChakraAccordion,
  ChakraAccordionButton,
  ChakraAccordionItem,
  ChakraAccordionPanel,
  ChevronRightIcon,
  Column,
  Combobox,
  ConfirmationDialog,
  FormField,
  Row,
  SegmentedControl,
  SegmentedControlOption,
  Select,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { useFormContext, useWatch } from "react-hook-form";

import {
  DEFAULT_CONSENT_PURGE_MODE,
  type ConsentPurgeMode,
  type ConsentPurgeValidationError,
} from "@hightouch/lib/customer-data/decision-engine/consent-purge";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useDecisionEngineRevocationsModelsQuery } from "src/graphql";
import { getColumnTypeLabel } from "src/pages/schema/columns/columns-utils";
import type { FeatureMatrixFormValues } from "src/pages/decision-engines/configuration/feature-matrices/types";
import {
  consentPurgeForModel,
  SANITIZE_PERMISSION,
  SHOULD_DIRTY_OPTION,
} from "src/pages/decision-engines/configuration/feature-matrices/utils";

const UNAUTHORIZED_TOOLTIP =
  "You need to be a workspace admin to change consent settings";

type Props = {
  /** `connection_id` of the decision engine's source, from the matrix's parent model. */
  sourceId: string | undefined;
  savedMode: ConsentPurgeMode;
  /** Validation problems from the last save attempt. */
  errors: ConsentPurgeValidationError[];
  onClearErrors: (fields: ConsentPurgeValidationError["field"][]) => void;
};

export function ConsentSettings({
  sourceId,
  savedMode,
  errors,
  onClearErrors,
}: Props) {
  const { control, setValue } = useFormContext<FeatureMatrixFormValues>();
  const consentPurge = useWatch({ control, name: "consentPurge" });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConfirmingExecute, setIsConfirmingExecute] = useState(false);
  const { isPermitted, isLoading: isPermissionLoading } =
    useResourcePermission(SANITIZE_PERMISSION);

  useEffect(() => {
    if (errors.length > 0) {
      setIsExpanded(true);
    }
  }, [errors]);

  const modelsQuery = useDecisionEngineRevocationsModelsQuery(
    { sourceId: sourceId ?? "" },
    { enabled: Boolean(sourceId), select: (data) => data.segments },
  );

  const selectedModel = modelsQuery.data?.find(
    (model) => String(model.id) === consentPurge?.revocationsModelId,
  );

  const columnOptions = useMemo(
    () =>
      (selectedModel?.columns ?? []).map((column) => ({
        label: column.name,
        value: column.name,
        description: column.type ? getColumnTypeLabel(column.type) : undefined,
      })),
    [selectedModel],
  );

  const errorFor = (field: ConsentPurgeValidationError["field"]) =>
    errors.find((error) => error.field === field)?.message;

  const modelError =
    errorFor("revocationsModel") ??
    (modelsQuery.isError
      ? "Couldn't load models for this source. Reload the page to try again."
      : undefined);

  const onModelChange = (modelId: string | undefined) => {
    onClearErrors(["revocationsModel", "primaryKey", "revokedAt"]);
    setValue(
      "consentPurge",
      consentPurgeForModel(consentPurge ?? null, modelId, savedMode),
      SHOULD_DIRTY_OPTION,
    );
  };

  return (
    <ChakraAccordion
      allowToggle
      index={isExpanded ? 0 : -1}
      onChange={(index) => setIsExpanded(index === 0)}
      flexShrink={0}
      borderBottom="1px solid"
      borderColor="base.border"
    >
      <ChakraAccordionItem border="none">
        <ChakraAccordionButton type="button" px={6} py={3}>
          <Row gap={2} align="center">
            <Box
              as={ChevronRightIcon}
              boxSize={4}
              flexShrink={0}
              transform={isExpanded ? "rotate(90deg)" : undefined}
              transitionProperty="transform"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              _mediaReduceMotion={{ transition: "none" }}
            />
            <Text fontWeight="medium">Consent settings</Text>
            <ConsentStatusBadge />
          </Row>
        </ChakraAccordionButton>
        <ChakraAccordionPanel px={6} pt={2} pb={4}>
          <Column gap={3}>
            <Text size="sm" color="text.secondary">
              Customers returned by this model have revoked consent. Choose
              below whether Hightouch removes their flagged features from stored
              history.
            </Text>

            <Row gap={4} align="flex-start" flexWrap="wrap">
              <Box flex="2 1 320px" minWidth="3xs">
                <FormField label="Revocations model" error={modelError}>
                  <AdminOnly isPermitted={isPermitted || isPermissionLoading}>
                    <Combobox
                      isDisabled={!isPermitted}
                      isInvalid={Boolean(modelError)}
                      isLoading={modelsQuery.isLoading}
                      isClearable
                      placeholder="Search models"
                      value={consentPurge?.revocationsModelId}
                      onChange={(value) => onModelChange(value ?? undefined)}
                      optionValue={(option) => String(option.id)}
                      optionLabel={(option) => option.name}
                      options={modelsQuery.data ?? []}
                    />
                  </AdminOnly>
                </FormField>
              </Box>

              <Box flex="1 1 200px" minWidth="4xs">
                <FormField
                  label="User ID column"
                  error={errorFor("primaryKey")}
                  description={
                    selectedModel
                      ? undefined
                      : "Available after you link a model"
                  }
                >
                  <AdminOnly isPermitted={isPermitted || isPermissionLoading}>
                    <Select
                      isDisabled={!isPermitted || !selectedModel}
                      isInvalid={Boolean(errorFor("primaryKey"))}
                      placeholder="Select a column"
                      options={columnOptions}
                      value={consentPurge?.primaryKey || undefined}
                      onChange={(value) => {
                        onClearErrors(["primaryKey"]);
                        setValue(
                          "consentPurge.primaryKey",
                          value ?? "",
                          SHOULD_DIRTY_OPTION,
                        );
                      }}
                    />
                  </AdminOnly>
                </FormField>
              </Box>

              <Box flex="1 1 200px" minWidth="4xs">
                <FormField
                  label="Revoked at column"
                  error={errorFor("revokedAt")}
                  description={
                    selectedModel
                      ? undefined
                      : "Available after you link a model"
                  }
                >
                  <AdminOnly isPermitted={isPermitted || isPermissionLoading}>
                    <Select
                      isDisabled={!isPermitted || !selectedModel}
                      isInvalid={Boolean(errorFor("revokedAt"))}
                      placeholder="Select a column"
                      options={columnOptions}
                      value={consentPurge?.revokedAt || undefined}
                      onChange={(value) => {
                        onClearErrors(["revokedAt"]);
                        setValue(
                          "consentPurge.revokedAt",
                          value ?? "",
                          SHOULD_DIRTY_OPTION,
                        );
                      }}
                    />
                  </AdminOnly>
                </FormField>
              </Box>

              <Box flex="1 1 200px" minWidth="4xs">
                <FormField
                  label="What a purge does"
                  description={
                    selectedModel
                      ? undefined
                      : "Available after you link a model"
                  }
                >
                  <AdminOnly isPermitted={isPermitted || isPermissionLoading}>
                    <SegmentedControl
                      width="100%"
                      value={consentPurge?.mode ?? DEFAULT_CONSENT_PURGE_MODE}
                      onChange={(mode: ConsentPurgeMode) => {
                        // Confirmed rather than set outright: this is the click that
                        // arms a daily irreversible purge, and the badge flips on it.
                        if (mode === "execute") {
                          setIsConfirmingExecute(true);
                          return;
                        }
                        setValue(
                          "consentPurge.mode",
                          mode,
                          SHOULD_DIRTY_OPTION,
                        );
                      }}
                      isDisabled={!isPermitted || !selectedModel}
                    >
                      <SegmentedControlOption
                        value={"plan" satisfies ConsentPurgeMode}
                        label="Report only"
                        tooltip="Removes nothing. Each daily run records what a purge would cover in its run logs."
                      />
                      <SegmentedControlOption
                        value={"execute" satisfies ConsentPurgeMode}
                        label="Remove the data"
                        tooltip="Clears flagged features for customers who revoked consent. This can't be undone."
                      />
                    </SegmentedControl>
                  </AdminOnly>
                </FormField>
              </Box>
            </Row>
          </Column>
        </ChakraAccordionPanel>
      </ChakraAccordionItem>

      <ConfirmationDialog
        isOpen={isConfirmingExecute}
        variant="danger"
        title="Remove stored features"
        confirmButtonText="Remove the data"
        onClose={() => setIsConfirmingExecute(false)}
        onConfirm={() => {
          setValue("consentPurge.mode", "execute", SHOULD_DIRTY_OPTION);
          setIsConfirmingExecute(false);
        }}
      >
        <Text>
          Every daily run clears the flagged features of each customer this
          model returns, in stored history as well as going forward. Hightouch
          keeps no copy of what it clears, so this can't be undone.
        </Text>
      </ConfirmationDialog>
    </ChakraAccordion>
  );
}

/**
 * Isolated so a keystroke anywhere in the column table re-renders the badge alone,
 * not the whole consent section.
 */
function ConsentStatusBadge() {
  const { control } = useFormContext<FeatureMatrixFormValues>();
  const consentPurge = useWatch({ control, name: "consentPurge" });

  if (!consentPurge?.primaryKey || !consentPurge?.revokedAt) {
    return <Badge size="sm">Not configured</Badge>;
  }
  if (consentPurge.mode === "execute") {
    return (
      <Badge size="sm" variant="success">
        Removing data
      </Badge>
    );
  }
  return (
    <Badge size="sm" variant="warning">
      Reporting only
    </Badge>
  );
}

/**
 * Explains the disabled state to non-admins. `withPermission` is not an option for
 * these controls: `Combobox` and `Select` are generic in their option type, and the
 * HOC's `forwardRef` erases that inference.
 */
function AdminOnly({
  isPermitted,
  children,
}: {
  isPermitted: boolean;
  children: ReactElement;
}) {
  return (
    <Tooltip
      isDisabled={isPermitted}
      message={UNAUTHORIZED_TOOLTIP}
      placement="auto"
    >
      {children}
    </Tooltip>
  );
}
